import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  DollarSign,
  HeartPulse,
  Heart,
  Settings,
  Target,
  Shield,
  Cloud,
  Building2,
  Compass,
  BarChart3,
  Zap,
  Percent,
  Timer,
  Landmark,
  Sparkles,
  Workflow,
} from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Button } from "../components/ui/button";
import StructuredData from "../components/SEO/StructuredData";
import TrustedClientsMarquee from "../components/TrustedClientsMarquee";
import HeroWaveBackground from "../components/HeroWaveBackground";
import {
  HeroCursorGlow,
  useHeroAmbience,
} from "../components/HeroInteractiveAmbience";
import HeroProofPanel from "../components/HeroProofPanel";
import {
  viewportDefault,
  pickFade,
  pickStagger,
  scaleIn,
  fadeUpReduced,
} from "../lib/motionPresets";

const ACCENT = "#e76d38";

const trustedOrganizations = [
  {
    name: "Gateway Regional",
    logo: "/assets/images/partner_logos/GatewayLogo.png",
  },
  { name: "Vista Health", logo: "/assets/images/partner_logos/VistaLogo.png" },
  { name: "Randolph Health", logo: null },
  {
    name: "Tenor Health Foundation",
    logo: "/assets/images/partner_logos/TenorLogo.svg",
  },
  {
    name: "Commonwealth Health",
    logo: "/assets/images/partner_logos/CommonwealthLogo.svg",
  },
  {
    name: "Sharon Regional",
    logo: "/assets/images/partner_logos/sharonLogo.svg",
  },
  { name: "DHR Health", logo: "/assets/images/partner_logos/dhrlogo.png" },
  {
    name: "Mission Community Hospital",
    logo: "/assets/images/partner_logos/missionLogo.png",
  },
  {
    name: "Carrollton Regional Medical Center",
    logo: "/assets/images/partner_logos/crmcLogo.png",
  },
  {
    name: "St. Rose Hospital",
    logo: "/assets/images/partner_logos/stRoseLogo.png",
  },
];

const heroStats = [
  {
    kicker: "Operational leverage",
    num: "20–30%",
    headline: "Run leaner without betting the house",
    subline:
      "Typical cost compression across RCM and business-office operations.",
    icon: Percent,
  },
  {
    kicker: "When it has to ship",
    num: "7 days",
    headline: "Engagement to production, not a science project",
    subline: "Critical paths get working automation, not a six-month roadmap.",
    icon: Timer,
  },
  {
    kicker: "Trusted at scale",
    num: "100s",
    headline: "Hospitals & health systems nationwide",
    subline: "Trusted by organizations leaders already know nationwide.",
    icon: Landmark,
  },
  {
    kicker: "Proof in the P&L",
    num: "$2M+",
    headline: "Annual impact from one managed program",
    subline: "Line-item savings operations and finance teams can stand behind.",
    icon: DollarSign,
  },
];

const pillars = [
  {
    icon: DollarSign,
    title: "Revenue Cycle Intelligence",
    emphasize: true,
    desc: "Coding quality, prior authorization, denials, and payment velocity—modernized with automation, analytics, and AI-assisted decision support where it improves throughput and cash.",
  },
  {
    icon: HeartPulse,
    title: "Clinical & Care Operations",
    emphasize: true,
    desc: "Readmission reduction, sepsis surveillance, CDI support, and care pathways—with AI-assisted tooling where governance and outcomes criteria are clear.",
  },
  {
    icon: Settings,
    title: "Business Office Automation",
    desc: "Scheduling, supply chain, workforce, and finance workflows—streamlined with integration, rules, and reporting your teams can run.",
  },
  {
    icon: Target,
    title: "Digital Strategy & Governance",
    desc: "Roadmaps, build-vs-buy decisions, ROI models, and program governance so investments land safely in production—including responsible AI adoption where it fits your risk profile.",
  },
  {
    icon: Shield,
    title: "Cybersecurity & Compliance",
    desc: "Assessments, threat visibility, incident readiness, automated compliance reporting, and IoMT protection aligned to healthcare risk.",
  },
];

const aiProgramCards = [
  {
    icon: Sparkles,
    title: "Revenue cycle & business office AI",
    desc: "Automate prior auth, coding quality, and denials—wired into your EHR and RCM stack for faster cash and cleaner claims.",
    points: ["Faster payer turnaround"],
    href: "/what-we-do#healthcare",
  },
  {
    icon: HeartPulse,
    title: "Clinical intelligence & CDI",
    desc: "Surveillance and documentation support that help care teams catch risk earlier and close notes on time.",
    points: ["Reduced LOS risk"],
    href: "/what-we-do#healthcare",
  },
  {
    icon: Shield,
    title: "Security analytics & automation",
    desc: "Correlate device, network, and EHR signals into response playbooks that cut alert noise and contain threats faster.",
    points: ["Fewer false positives"],
    href: "/what-we-do#cybersecurity",
  },
];

const coreServices = [
  {
    icon: Sparkles,
    title: "AI Transformation Services",
    desc: "Identify, prioritize, and deploy healthcare AI use cases from pilot to production with measurable ROI.",
    points: ["90-day AI deployment roadmap"],
    href: "/what-we-do#ai-transformation",
  },
  {
    icon: Heart,
    title: "Healthcare IT Services",
    desc: "EHR support, infrastructure ops, and analytics that keep clinical and business systems running.",
    points: ["99.9% uptime target"],
    href: "/what-we-do#healthcare",
  },
  {
    icon: Shield,
    title: "Cyber Security Solutions",
    desc: "Healthcare risk assessments, IoMT protection, and ransomware readiness built for HIPAA environments.",
    points: ["7-day recovery playbook"],
    href: "/what-we-do#cybersecurity",
  },
  {
    icon: Cloud,
    title: "Cloud & Advisory Services",
    desc: "Cloud migration, modernization, and executive advisory for vendor strategy, M&A, and cost optimization.",
    points: ["25–40% infra cost cuts + contract savings"],
    href: "/what-we-do#cloud",
  },
];

const operatorBadges = [
  "Former Health System CIOs",
  "Former Health System CISOs",
  "Former Health System CTOs",
];

const operatorPoints = [
  {
    icon: Building2,
    title: "Production-ready, not pilot-ready",
    metric: "Zero stalled pilots",
    text: "Playbooks refined across 15 hospitals and 250+ clinics—not slide decks.",
    detail:
      "Every engagement targets a production cutover with KPIs agreed on day one.",
  },
  {
    icon: Compass,
    title: "Vendor-agnostic by design",
    metric: "100% independent",
    text: "Recommendations fit your EHR, budget, and clinical workflows—never a preferred-partner quota.",
    detail:
      "We take no reseller margin. Selections are scored against your environment, not ours.",
  },
  {
    icon: Zap,
    title: "Speed when it matters",
    metric: "7-day recovery",
    text: "Ransomware recovery and M&A transitions restored to full operations in a week.",
    detail:
      "On-call operator bench with healthcare-grade runbooks ready before you need them.",
  },
];

const operatorCredibility = [
  { value: "150+", label: "Years combined operator experience" },
  { value: "300+", label: "Healthcare programs shipped" },
  { value: "100%", label: "Vendor-independent recommendations" },
];

const caseStudyHighlights = [
  {
    sector: "Multi-state hospital network",
    metric: "25%",
    label: "IT operating cost reduction",
    detail: "12-month rollout across managed services and infrastructure",
    icon: Building2,
  },
  {
    sector: "Regional health system",
    metric: "41%",
    label: "Workflow efficiency gain",
    detail: "Revenue cycle automation across 8 hospital departments",
    icon: Workflow,
  },
  {
    sector: "Specialty care provider",
    metric: "54%",
    label: "Manual workload eliminated",
    detail: "Clinical documentation and prior auth workflows",
    icon: HeartPulse,
  },
];

const techPartners = [
  {
    name: "Microsoft Azure",
    logo: "/assets/images/tech_logos/azure.svg",
  },
  { name: "AWS", logo: "/assets/images/tech_logos/aws.svg" },
  { name: "Epic", logo: "/assets/images/tech_logos/epic.svg" },
  {
    name: "Oracle Health",
    logo: "/assets/images/tech_logos/oracle-health.svg",
  },
  { name: "Meditech", logo: "/assets/images/tech_logos/meditech.svg" },
  { name: "Workday", logo: "/assets/images/tech_logos/workday.svg" },
  { name: "ServiceNow", logo: "/assets/images/tech_logos/servicenow.svg" },
  { name: "Snowflake", logo: "/assets/images/tech_logos/snowflake.svg" },
];

function WhatWeDoEditorialRail({ services, fade, stagger }) {
  return (
    <div className="mx-auto max-w-[1280px]">
      <motion.div variants={fade} className="max-w-3xl">
        <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-complement-600">
          What we do
        </p>
        <h2 className="mt-3 text-[clamp(1.625rem,3vw,2.35rem)] font-extrabold tracking-tight text-shark-950">
          End-to-end healthcare IT services
        </h2>
        <p className="mt-4 text-[1.02rem] leading-relaxed text-shark-500">
          From EHR support and infrastructure ops to cloud migration and
          advisory—four service lines proven to reduce cost, improve uptime,
          and strengthen clinical workflows.
        </p>
        <Link
          to="/what-we-do"
          className="mt-6 inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-shark-400 transition-colors hover:text-complement-700"
        >
          See all service lines
          <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </motion.div>

      <motion.div variants={stagger} className="mt-8">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {services.map((service) => (
            <motion.article
              key={service.title}
              variants={fade}
              className="scroll-mt-28"
            >
              <Link
                to={service.href}
                className="group block h-full rounded-2xl border border-shark-200 bg-white p-5 shadow-sm transition-all sm:p-6 hover:-translate-y-0.5 hover:border-complement-300 hover:ring-1 hover:ring-complement-300/60 hover:shadow-[0_26px_78px_-48px_rgba(231,109,56,0.58)]"
              >
                <div
                  className="flex h-11 w-11 items-center justify-center rounded-xl text-white shadow-sm"
                  style={{
                    background: `linear-gradient(135deg, ${ACCENT}, #b2381c)`,
                  }}
                >
                  <service.icon className="h-5 w-5" />
                </div>
                <h3 className="mt-4 text-[1.05rem] font-bold leading-snug text-shark-900">
                  {service.title}
                </h3>
                <p className="mt-2 text-[0.9rem] leading-relaxed text-shark-500">
                  {service.desc}
                </p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {service.points.map((point) => (
                    <span
                      key={point}
                      className="rounded-md border border-shark-200 bg-shark-50 px-2.5 py-1 text-[11px] font-semibold text-shark-600"
                    >
                      {point}
                    </span>
                  ))}
                </div>
                <div className="mt-5 inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.14em] text-complement-700">
                  Explore
                  <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
                </div>
              </Link>
            </motion.article>
          ))}
        </div>
      </motion.div>
    </div>
  );
}

function LandingPage() {
  const reduceMotion = useReducedMotion();
  const heroAmbience = useHeroAmbience();
  const fade = pickFade(reduceMotion);
  const stagger = pickStagger(reduceMotion);
  const ctaReveal = reduceMotion ? fadeUpReduced : scaleIn;

  return (
    <>
      <Helmet>
        <html lang="en" />
        <title>
          Velozent — Transform Health System Operations with AI Driven IT Strategy
        </title>
        <meta
          name="description"
          content="Velozent helps healthcare organizations streamline workflows, reduce manual effort, and modernize IT—without disrupting care delivery."
        />
        <meta
          name="keywords"
          content="healthcare IT services, health IT consulting, managed IT services, cloud migration, cybersecurity, healthcare AI, intelligent automation, advisory services, Velozent"
        />
        <meta name="author" content="Velozent" />
        <meta name="robots" content="index, follow" />
        <meta
          property="og:title"
          content="Velozent — Transform Health System Operations with AI Driven IT Strategy"
        />
        <meta
          property="og:description"
          content="Velozent helps healthcare organizations streamline workflows, reduce manual effort, and modernize IT—without disrupting care delivery."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://velozent.com/" />
        <meta
          property="og:image"
          content="https://velozent.com/assets/images/logo.svg"
        />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="Velozent" />
        <meta property="og:locale" content="en_US" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Velozent — Transform Health System Operations with AI Driven IT Strategy"
        />
        <meta
          name="twitter:description"
          content="Velozent helps healthcare organizations streamline workflows, reduce manual effort, and modernize IT—without disrupting care delivery."
        />
        <meta
          name="twitter:image"
          content="https://velozent.com/assets/images/logo.svg"
        />
        <link rel="canonical" href="https://velozent.com/" />
      </Helmet>
      <StructuredData type="Organization" />
      <StructuredData type="WebSite" />
      <StructuredData type="LocalBusiness" />

      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <section
        className="hero-gradient px-4 pb-16 pt-24 text-white sm:px-6 sm:pb-20 sm:pt-28 lg:px-8 lg:pb-24 lg:pt-32"
        {...heroAmbience.handlers}
      >
        <div className="hero-gradient-grid" aria-hidden />
        <div className="hero-gradient-grain" aria-hidden />
        <HeroWaveBackground variant="landing" extended />
        <HeroCursorGlow backgroundImage={heroAmbience.spotlightBg} />

        <div className="hero-gradient-inner mx-auto max-w-[1160px]">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-10 lg:gap-x-12 xl:gap-x-14">
            {/* Left — headline & CTAs */}
            <div className="lg:col-span-7 lg:[perspective:1280px] lg:[perspective-origin:38%_18%]">
              <motion.div
                variants={stagger}
                initial="hidden"
                animate="visible"
                className="transform-gpu will-change-transform [transform-style:preserve-3d]"
                style={{
                  rotateX: heroAmbience.tiltLeft.rotateX,
                  rotateY: heroAmbience.tiltLeft.rotateY,
                }}
              >
                <motion.h1
                  variants={fade}
                  className="max-w-[900px] text-balance text-[clamp(2rem,4.6vw,3.35rem)] font-extrabold leading-[1.08] tracking-tight"
                >
                  Transform Health System Operations with{" "}
                  <span style={{ color: ACCENT }}>AI Driven IT Strategy</span>
                </motion.h1>

                <motion.div variants={fade} className="mt-7 max-w-[640px]">
                  <p className="text-[1.0625rem] leading-relaxed text-white/75">
                    Modernize EHR, revenue cycle, and clinical workflows—reduce
                    IT spend, eliminate manual work, and free your teams to
                    focus on care.
                  </p>
                </motion.div>

                <motion.p
                  variants={fade}
                  className="mt-5 max-w-[640px] text-[0.95rem] font-semibold leading-relaxed text-white/70"
                >
                  Health systems we work with see up to{" "}
                  <span className="text-white">25% IT cost reduction</span> and{" "}
                  <span className="text-white">50%+ less manual workload</span>{" "}
                  in the first year.
                </motion.p>

                <motion.div
                  aria-hidden
                  variants={fade}
                  className="mt-8 h-px w-full max-w-[min(100%,26rem)] rounded-full bg-gradient-to-r from-transparent via-white/25 to-transparent"
                />

                <motion.div variants={fade} className="mt-9">
                  <Button
                    asChild
                    size="lg"
                    className="group h-[52px] border-0 px-8 text-[15px] font-bold text-white shadow-[0_8px_36px_-8px_rgba(231,109,56,0.75)] transition-[transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:shadow-[0_14px_44px_-8px_rgba(231,109,56,0.55)]"
                    style={{ backgroundColor: ACCENT }}
                  >
                    <Link to="/contact">
                      See Your IT Cost Savings in 15 Minutes
                      <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                    </Link>
                  </Button>
                </motion.div>
              </motion.div>
            </div>

            {/* Right — rotating proof stat */}
            <motion.div
              className="lg:col-span-5 lg:flex lg:items-center lg:justify-center"
              variants={fade}
              initial="hidden"
              animate="visible"
            >
              <div className="w-full lg:[perspective:1100px] lg:[perspective-origin:55%_45%] lg:w-auto">
                <motion.div
                  className="w-full transform-gpu will-change-transform lg:w-auto [transform-style:preserve-3d]"
                  style={{
                    rotateX: heroAmbience.tiltRight.rotateX,
                    rotateY: heroAmbience.tiltRight.rotateY,
                  }}
                >
                  <div className="w-full lg:w-auto lg:translate-x-4 xl:translate-x-6 2xl:translate-x-8">
                    <HeroProofPanel stats={heroStats} accent={ACCENT} />
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── Healthcare IT services (primary scope) ───────────────────────── */}
      <motion.section
        className="bg-white px-4 py-16 lg:py-24"
        variants={stagger}
        initial="hidden"
        whileInView="visible"
        viewport={viewportDefault}
      >
        <WhatWeDoEditorialRail
          services={coreServices}
          fade={fade}
          stagger={stagger}
        />

        {/* Intelligent automation — outside split grid to avoid sticky overlap */}
        <div className="mx-auto mt-12 max-w-[1280px] border-t border-shark-200 pt-10">
          <motion.div
            variants={fade}
            initial="hidden"
            whileInView="visible"
            viewport={viewportDefault}
            className="mb-6"
          >
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-burnt-sienna-600">
              Intelligent automation &amp; AI
            </p>
            <h3 className="mt-2 text-[clamp(1.25rem,2.2vw,1.75rem)] font-extrabold tracking-tight text-shark-950">
              AI that works inside your EHR, RCM, and security stack
            </h3>
          </motion.div>

          <div className="relative overflow-hidden rounded-[1.75rem] border border-shark-800/70 bg-gradient-to-br from-shark-950 via-[#122b38] to-[#0d1f2c] px-6 py-10 shadow-[0_28px_90px_-32px_rgba(0,0,0,0.45)] sm:px-9 sm:py-11 lg:px-11 lg:py-14">
            <div
              className="pointer-events-none absolute inset-0 opacity-[0.09]"
              style={{
                backgroundImage:
                  "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.45) 1px, transparent 0)",
                backgroundSize: "28px 28px",
              }}
              aria-hidden
            />
            <div
              className="pointer-events-none absolute -right-24 top-1/2 h-[min(420px,55vw)] w-[min(420px,55vw)] -translate-y-1/2 rounded-full bg-burnt-sienna-500/20 blur-[100px]"
              aria-hidden
            />
            <div
              className="pointer-events-none absolute -left-16 bottom-0 h-48 w-48 rounded-full bg-complement-500/15 blur-[70px]"
              aria-hidden
            />

            <div className="relative grid gap-10 lg:grid-cols-12 lg:gap-12 lg:items-start">
              <div className="lg:col-span-5">
                <h3 className="mt-3 text-[clamp(1.35rem,2.5vw,1.85rem)] font-extrabold leading-[1.15] tracking-tight text-white">
                  AI agents that work alongside your teams
                </h3>
                <p className="mt-5 max-w-xl text-[1.02rem] leading-relaxed text-white/80">
                  Integrated across leading EHRs, ERPs, RCM platforms, and
                  security stacks—with healthcare-grade controls and runbooks
                  so AI lands in throughput, cash flow, and risk reduction.
                </p>
                <div className="mt-5 flex flex-wrap gap-2">
                  {pillars.slice(0, 3).map((p) => (
                    <span
                      key={p.title}
                      className="rounded-md border border-white/10 bg-white/[0.06] px-2.5 py-1 text-[11px] font-semibold text-white/75"
                    >
                      {p.title}
                    </span>
                  ))}
                </div>
                <Link
                  to="/what-we-do"
                  className="mt-7 inline-flex items-center gap-2 text-[12px] font-bold uppercase tracking-[0.14em] text-burnt-sienna-200 transition-colors hover:text-white"
                >
                  View all capabilities
                  <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </div>

              <div className="lg:col-span-7">
                <div className="overflow-hidden rounded-2xl border border-white/12 bg-white/[0.04] shadow-inner backdrop-blur-sm">
                  {aiProgramCards.map((s, i) => {
                    const Icon = s.icon;
                    return (
                      <div key={s.title}>
                        <Link
                          to={s.href}
                          className="group flex gap-4 border-b border-white/10 px-4 py-5 transition-colors last:border-b-0 hover:bg-white/[0.06] sm:gap-5 sm:px-5 sm:py-6"
                        >
                          <span className="select-none font-mono text-[2rem] font-black leading-none text-white/[0.12] transition-colors tabular-nums group-hover:text-burnt-sienna-400/85 sm:text-[2.25rem]">
                            {String(i + 1).padStart(2, "0")}
                          </span>
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-burnt-sienna-500/20 text-burnt-sienna-200 ring-1 ring-burnt-sienna-400/25">
                                <Icon className="h-4 w-4" strokeWidth={2} />
                              </span>
                              <h4 className="text-[1.02rem] font-bold leading-snug text-white">
                                {s.title}
                              </h4>
                            </div>
                            <p className="mt-2 text-[0.8125rem] leading-relaxed text-white/60">
                              {s.desc}
                            </p>
                            <div className="mt-3 flex flex-wrap gap-2">
                              {s.points.map((point) => (
                                <span
                                  key={point}
                                  className="rounded-md border border-white/10 bg-white/[0.06] px-2.5 py-1 text-[11px] font-semibold text-white/75"
                                >
                                  {point}
                                </span>
                              ))}
                            </div>
                          </div>
                          <ArrowRight className="mt-1 h-5 w-5 shrink-0 text-white/25 transition-all group-hover:translate-x-0.5 group-hover:text-burnt-sienna-200" />
                        </Link>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.section>

      {/* ── Operator band ───────────────────────────────────────────────── */}
      <section className="operator-band px-4 py-14 text-white lg:py-20">
        <div className="mx-auto grid max-w-[1160px] grid-cols-1 items-start gap-12 lg:grid-cols-2 lg:gap-20">
          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={viewportDefault}
          >
            <motion.p
              variants={fade}
              className="text-[11px] font-bold uppercase tracking-[0.2em]"
              style={{ color: ACCENT }}
            >
              Why Velozent
            </motion.p>
            <motion.h2
              variants={fade}
              className="mt-4 text-[clamp(1.625rem,3vw,2.5rem)] font-extrabold leading-[1.12] tracking-tight"
            >
              Healthcare Operators,
              <br />
              <span className="font-semibold text-white/55">
                Not Outside Consultants
              </span>
            </motion.h2>
            <motion.p
              variants={fade}
              className="mt-5 text-[1.0625rem] leading-relaxed text-white/70"
            >
              Velozent was founded by former health system CIOs, CISOs, and
              CTOs who&apos;ve sat in the chair you&apos;re sitting in
              now—facing board pressure on cost, regulatory scrutiny on data,
              and clinical escalations on uptime.
            </motion.p>
            <motion.p
              variants={fade}
              className="mt-4 text-[1.0625rem] leading-relaxed text-white/60"
            >
              Our playbooks start with what actually ships in a live
              hospital—not what sounds good on a slide.
            </motion.p>

            <motion.div variants={fade} className="mt-7 flex flex-wrap gap-2.5">
              {operatorBadges.map((b) => (
                <span
                  key={b}
                  className="rounded-full border px-3.5 py-1 text-[12px] font-semibold"
                  style={{
                    borderColor: `${ACCENT}50`,
                    backgroundColor: `${ACCENT}18`,
                    color: ACCENT,
                  }}
                >
                  {b}
                </span>
              ))}
            </motion.div>

            <motion.div
              variants={fade}
              className="mt-8 grid grid-cols-3 gap-3 rounded-2xl border border-white/10 bg-white/[0.03] p-4 sm:gap-5 sm:p-5"
            >
              {operatorCredibility.map((c) => (
                <div key={c.label} className="flex flex-col gap-1">
                  <span className="font-mono text-[1.5rem] font-extrabold leading-none tabular-nums text-white sm:text-[1.7rem]">
                    {c.value}
                  </span>
                  <span className="text-[10.5px] font-semibold uppercase leading-tight tracking-[0.12em] text-white/55 sm:text-[11px]">
                    {c.label}
                  </span>
                </div>
              ))}
            </motion.div>
          </motion.div>

          <motion.div
            className="flex flex-col gap-3.5"
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={viewportDefault}
          >
            {operatorPoints.map((op, idx) => {
              const Icon = op.icon;
              return (
                <motion.div
                  key={op.title}
                  variants={fade}
                  whileHover={
                    reduceMotion
                      ? undefined
                      : { backgroundColor: "rgba(255,255,255,0.09)" }
                  }
                  className="flex gap-5 rounded-2xl border border-white/8 bg-white/[0.05] p-5 transition-colors duration-300"
                >
                  <div className="flex flex-col items-center gap-2">
                    <div
                      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-[13px] font-extrabold"
                      style={{
                        backgroundColor: `${ACCENT}28`,
                        color: ACCENT,
                      }}
                    >
                      {String(idx + 1).padStart(2, "0")}
                    </div>
                    <Icon
                      className="h-4 w-4 text-white/30"
                      strokeWidth={2}
                      aria-hidden
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h4 className="font-bold text-white">{op.title}</h4>
                      <span
                        className="rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.12em]"
                        style={{
                          backgroundColor: `${ACCENT}1f`,
                          color: ACCENT,
                          border: `1px solid ${ACCENT}44`,
                        }}
                      >
                        {op.metric}
                      </span>
                    </div>
                    <p className="mt-1.5 text-[0.9rem] leading-relaxed text-white/70">
                      {op.text}
                    </p>
                    <p className="mt-2 text-[0.8125rem] leading-relaxed text-white/45">
                      {op.detail}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* ── Case study highlights ───────────────────────────────────────── */}
      <section className="bg-white px-4 py-14 lg:py-20">
        <div className="mx-auto max-w-[1280px]">
          <motion.div
            initial={reduceMotion ? false : { opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={viewportDefault}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-end"
          >
            <div className="max-w-xl">
              <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-complement-600">
                Recent results
              </p>
              <h2 className="mt-2 text-[clamp(1.5rem,2.6vw,2rem)] font-extrabold leading-[1.15] tracking-tight text-shark-950">
                Outcomes from real healthcare implementations
              </h2>
            </div>
            <Link
              to="/contact"
              className="inline-flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-[0.14em] text-complement-700 transition-colors hover:text-complement-900"
            >
              Talk to our team
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </motion.div>

          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={viewportDefault}
            className="mt-8 overflow-hidden rounded-2xl border border-shark-200 bg-white shadow-sm"
          >
            <div className="hidden grid-cols-[2.1fr_1fr_2.1fr_2.2fr] gap-4 border-b border-shark-200/80 bg-shark-50/80 px-6 py-3 text-[10px] font-bold uppercase tracking-[0.14em] text-shark-500 lg:grid">
              <span>Implementation context</span>
              <span>Outcome</span>
              <span>Business impact</span>
              <span>Delivery scope</span>
            </div>

            {caseStudyHighlights.map((c) => {
              const Icon = c.icon;
              return (
                <motion.article
                  key={c.label}
                  variants={fade}
                  className="group border-b border-shark-100 px-5 py-5 last:border-b-0 lg:px-6 lg:py-4"
                >
                  <div className="grid items-start gap-3 lg:grid-cols-[2.1fr_1fr_2.1fr_2.2fr] lg:gap-4">
                    <div className="flex items-center gap-3">
                      <span
                        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-white shadow-sm"
                        style={{
                          background: `linear-gradient(135deg, ${ACCENT}, #b2381c)`,
                        }}
                      >
                        <Icon className="h-3.5 w-3.5" strokeWidth={2.2} />
                      </span>
                      <p className="text-[0.86rem] font-semibold leading-snug text-shark-700">
                        {c.sector}
                      </p>
                    </div>

                    <p className="font-mono text-[1.35rem] font-extrabold leading-none tabular-nums text-shark-950 md:text-[1.45rem]">
                      {c.metric}
                    </p>

                    <p className="text-[0.92rem] font-bold leading-snug text-shark-900">
                      {c.label}
                    </p>

                    <p className="text-[0.83rem] leading-relaxed text-shark-500">
                      {c.detail}
                    </p>
                  </div>
                </motion.article>
              );
            })}
          </motion.div>

          <p className="mt-5 text-[11px] font-medium uppercase tracking-[0.14em] text-shark-400">
            Based on recent healthcare implementations · representative outcomes
          </p>
        </div>
      </section>

      {/* ── Social proof ────────────────────────────────────────────────── */}
      <section className="border-t border-shark-200/80 bg-[#f4f7f9] py-14 lg:py-20">
        <div className="mx-auto max-w-[1280px] px-4">
          {/* Header */}
          <motion.div
            className="mx-auto max-w-[720px] text-center"
            initial={reduceMotion ? false : { opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={viewportDefault}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          >
            <p className="text-[12px] font-bold uppercase tracking-[0.22em] text-complement-600">
              Our clients
            </p>
            <h2 className="mt-4 text-[clamp(1.75rem,3.5vw,2.625rem)] font-bold leading-[1.15] tracking-tight text-shark-950">
              Trusted by{" "}
              <em className="font-serif text-[1.05em] font-semibold italic text-complement-700">
                leading
              </em>{" "}
              healthcare organizations
            </h2>
            <p className="mx-auto mt-4 max-w-[520px] text-[1rem] leading-relaxed text-shark-500">
              Running the IT systems that keep 15 hospitals, 250+ clinics, and
              15M+ patients moving every day.
            </p>
          </motion.div>
        </div>

        {/* Full-bleed marquee — OP-style drifting logo row */}
        <TrustedClientsMarquee organizations={trustedOrganizations} />

        <div className="mx-auto max-w-[1280px] px-4">
          {/* Tech platforms */}
          <motion.div
            className="mt-12"
            initial={reduceMotion ? false : { opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={viewportDefault}
            transition={{ duration: 0.5 }}
          >
            <p className="mb-6 text-center text-[11px] font-bold uppercase tracking-[0.2em] text-shark-400">
              Certified &amp; experienced across leading platforms
            </p>
            <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-6 md:gap-x-14">
              {techPartners.map((t) => (
                <img
                  key={t.name}
                  src={t.logo}
                  alt={t.name}
                  className="h-7 w-auto max-w-[120px] object-contain opacity-50 grayscale transition duration-300 hover:opacity-90 hover:grayscale-0 md:h-8 md:max-w-[140px]"
                  loading="lazy"
                />
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── Final CTA ────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden border-t border-white/10 px-4 py-14 text-center lg:py-20">
        <div
          className="pointer-events-none absolute inset-0 bg-gradient-to-br from-complement-800 via-complement-950 to-[#070d11]"
          aria-hidden
        />
        <div
          className="pointer-events-none absolute -right-[20%] top-1/2 h-[min(500px,55vw)] w-[min(500px,55vw)] -translate-y-1/2 rounded-full bg-complement-500/20 blur-[110px]"
          aria-hidden
        />
        <div
          className="pointer-events-none absolute -bottom-24 left-[10%] h-72 w-72 rounded-full bg-complement-400/12 blur-[80px]"
          aria-hidden
        />
        <div
          className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_90%_60%_at_50%_-20%,rgba(255,255,255,0.05),transparent_55%)]"
          aria-hidden
        />

        <motion.div
          className="relative mx-auto max-w-[560px]"
          variants={ctaReveal}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.35 }}
        >
          <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-complement-300/75">
            Your next step
          </p>
          <h2 className="mt-3 text-[clamp(1.625rem,3vw,2.25rem)] font-extrabold tracking-tight text-white">
            See what 25% less IT spend looks like for your health system
          </h2>
          <p className="mt-4 text-[1.02rem] leading-relaxed text-white/60">
            Book a 15-minute call. We&apos;ll map your current IT cost,
            workflow friction, and AI opportunities against what we&apos;ve
            delivered across 15 hospitals and 250+ clinics.
          </p>
          <motion.div
            className="mt-7 flex flex-col items-center gap-3 sm:flex-row sm:justify-center"
            whileHover={reduceMotion ? undefined : { scale: 1.0 }}
            transition={{ type: "spring", stiffness: 400, damping: 25 }}
          >
            <Button
              asChild
              size="lg"
              className="h-[54px] border-0 bg-white px-10 text-[16px] font-bold text-complement-950 shadow-lg shadow-black/25 hover:bg-complement-50"
            >
              <Link to="/contact">
                Book Your 15-Minute IT Strategy Call
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </motion.div>
          <p className="mt-4 text-[12px] font-medium text-white/40">
            No obligation. No pitch deck. Just a conversation with an operator.
          </p>
        </motion.div>
      </section>
    </>
  );
}

export default LandingPage;
